import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import {
  QrCode, BarChart2, CheckSquare, MessageSquare,
  Shield, Building2, ChevronRight, Smartphone, ChevronLeft, Globe
} from 'lucide-react';

const WA_SVG = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* ── Slide Illustrations ─────────────────────────────────────── */
function PhoneIllustration() {
  return (
    <svg viewBox="0 0 260 480" width="220" height="400" style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))' }}>
      <rect x="0" y="0" width="260" height="480" rx="32" fill="#0f172a" />
      <rect x="8" y="36" width="244" height="396" fill="#fff" />
      <rect x="100" y="12" width="60" height="16" rx="8" fill="#1e293b" />
      <rect x="8" y="36" width="244" height="48" fill="#1d4ed8" />
      <text x="130" y="56" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="Arial,sans-serif">Memnuniyetinizi Paylaşın</text>
      <text x="130" y="70" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9" fontFamily="Arial,sans-serif">WC · 3. Kat · Blok A</text>
      <rect x="60" y="104" width="140" height="140" rx="12" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1.5" />
      <rect x="76" y="120" width="30" height="30" rx="3" fill="none" stroke="#0f172a" strokeWidth="3" />
      <rect x="154" y="120" width="30" height="30" rx="3" fill="none" stroke="#0f172a" strokeWidth="3" />
      <rect x="76" y="198" width="30" height="30" rx="3" fill="none" stroke="#0f172a" strokeWidth="3" />
      <rect x="85" y="129" width="12" height="12" rx="1" fill="#0f172a" />
      <rect x="163" y="129" width="12" height="12" rx="1" fill="#0f172a" />
      <rect x="85" y="207" width="12" height="12" rx="1" fill="#0f172a" />
      <rect x="112" y="120" width="36" height="4" rx="2" fill="#0f172a" opacity="0.6" />
      <rect x="112" y="128" width="28" height="4" rx="2" fill="#0f172a" opacity="0.3" />
      <rect x="112" y="136" width="36" height="4" rx="2" fill="#0f172a" opacity="0.6" />
      <rect x="76" y="158" width="108" height="4" rx="2" fill="#0f172a" opacity="0.3" />
      <rect x="76" y="166" width="108" height="4" rx="2" fill="#0f172a" opacity="0.2" />
      <rect x="76" y="174" width="80" height="4" rx="2" fill="#0f172a" opacity="0.2" />
      <text x="130" y="272" textAnchor="middle" fill="#64748b" fontSize="10" fontFamily="Arial,sans-serif">Lütfen değerlendirin</text>
      <text x="94" y="293" fontSize="18" fontFamily="Arial" fill="#fbbf24">★</text>
      <text x="112" y="293" fontSize="18" fontFamily="Arial" fill="#fbbf24">★</text>
      <text x="130" y="293" fontSize="18" fontFamily="Arial" fill="#fbbf24">★</text>
      <text x="148" y="293" fontSize="18" fontFamily="Arial" fill="#fbbf24">★</text>
      <text x="166" y="293" fontSize="18" fontFamily="Arial" fill="#e2e8f0">★</text>
      <rect x="50" y="306" width="160" height="36" rx="10" fill="#1d4ed8" />
      <text x="130" y="329" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="Arial,sans-serif">Gönder</text>
      <rect x="8" y="432" width="244" height="2" fill="#e2e8f0" />
      <rect x="100" y="444" width="60" height="4" rx="2" fill="#cbd5e1" />
    </svg>
  );
}

function DashboardIllustration() {
  const bars = [60, 85, 45, 95, 70, 88, 52];
  const days = ['Pts', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
  return (
    <svg viewBox="0 0 340 260" width="340" height="260" style={{ filter: 'drop-shadow(0 16px 32px rgba(0,0,0,0.25))' }}>
      <rect x="0" y="0" width="340" height="260" rx="14" fill="#fff" />
      <rect x="0" y="0" width="340" height="42" rx="14" fill="#f8fafc" />
      <rect x="0" y="28" width="340" height="14" fill="#f8fafc" />
      <circle cx="14" cy="18" r="5" fill="#ef4444" opacity="0.6" />
      <circle cx="26" cy="18" r="5" fill="#f59e0b" opacity="0.6" />
      <circle cx="38" cy="18" r="5" fill="#22c55e" opacity="0.6" />
      <text x="80" y="23" fill="#475569" fontSize="10" fontFamily="Arial,sans-serif">temiz-iz.com · Dashboard</text>
      <rect x="12" y="54" width="76" height="60" rx="8" fill="#eff6ff" />
      <text x="50" y="76" textAnchor="middle" fill="#1d4ed8" fontSize="20" fontWeight="800" fontFamily="Arial,sans-serif">%94</text>
      <text x="50" y="90" textAnchor="middle" fill="#64748b" fontSize="8" fontFamily="Arial,sans-serif">Memnuniyet</text>
      <rect x="96" y="54" width="76" height="60" rx="8" fill="#f0fdf4" />
      <text x="134" y="76" textAnchor="middle" fill="#16a34a" fontSize="20" fontWeight="800" fontFamily="Arial,sans-serif">1.245</text>
      <text x="134" y="90" textAnchor="middle" fill="#64748b" fontSize="8" fontFamily="Arial,sans-serif">Anket</text>
      <rect x="180" y="54" width="76" height="60" rx="8" fill="#fdf4ff" />
      <text x="218" y="76" textAnchor="middle" fill="#7c3aed" fontSize="20" fontWeight="800" fontFamily="Arial,sans-serif">48</text>
      <text x="218" y="90" textAnchor="middle" fill="#64748b" fontSize="8" fontFamily="Arial,sans-serif">Lokasyon</text>
      <rect x="264" y="54" width="64" height="60" rx="8" fill="#fff7ed" />
      <text x="296" y="76" textAnchor="middle" fill="#c2410c" fontSize="20" fontWeight="800" fontFamily="Arial,sans-serif">3</text>
      <text x="296" y="90" textAnchor="middle" fill="#64748b" fontSize="8" fontFamily="Arial,sans-serif">Düşük Puan</text>
      <text x="12" y="132" fill="#475569" fontSize="9" fontWeight="600" fontFamily="Arial,sans-serif">Haftalık Trend</text>
      {bars.map((h, i) => (
        <g key={i}>
          <rect x={12 + i * 44} y={200 - h * 0.7} width="28" height={h * 0.7} rx="4"
            fill={i === 3 ? '#1d4ed8' : '#bfdbfe'} />
          <text x={26 + i * 44} y="216" textAnchor="middle" fill="#94a3b8" fontSize="8" fontFamily="Arial,sans-serif">{days[i]}</text>
        </g>
      ))}
      <rect x="12" y="228" width="316" height="1" fill="#f1f5f9" />
      <rect x="12" y="236" width="80" height="8" rx="4" fill="#f1f5f9" />
      <rect x="100" y="236" width="60" height="8" rx="4" fill="#f1f5f9" />
      <rect x="168" y="236" width="40" height="8" rx="4" fill="#f1f5f9" />
    </svg>
  );
}

function ChecklistIllustration() {
  const items = [
    { text: 'Zemin temizlendi', done: true },
    { text: 'Sarf malzeme kontrolü', done: true },
    { text: 'Lavabo dezenfeksiyonu', done: true },
    { text: 'Havalandırma kontrolü', done: false },
    { text: 'Son kontrol fotoğrafı', done: false },
  ];
  return (
    <svg viewBox="0 0 280 360" width="240" height="308" style={{ filter: 'drop-shadow(0 16px 32px rgba(0,0,0,0.25))' }}>
      <rect x="0" y="0" width="280" height="360" rx="16" fill="#fff" />
      <rect x="0" y="0" width="280" height="56" rx="16" fill="#1d4ed8" />
      <rect x="0" y="42" width="280" height="14" fill="#1d4ed8" />
      <text x="140" y="26" textAnchor="middle" fill="white" fontSize="12" fontWeight="700" fontFamily="Arial,sans-serif">Temizlik Kontrol Listesi</text>
      <text x="140" y="42" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9" fontFamily="Arial,sans-serif">WC · B Blok · 16 Nisan 2026</text>
      <text x="20" y="78" fill="#475569" fontSize="9" fontFamily="Arial,sans-serif">Tamamlandı: 3 / 5</text>
      <rect x="20" y="86" width="240" height="8" rx="4" fill="#e2e8f0" />
      <rect x="20" y="86" width="144" height="8" rx="4" fill="#22c55e" />
      {items.map((item, i) => (
        <g key={i}>
          <rect x="20" y={108 + i * 44} width="240" height="36" rx="8"
            fill={item.done ? '#f0fdf4' : '#fff'}
            stroke={item.done ? '#bbf7d0' : '#e2e8f0'} strokeWidth="1" />
          <rect x="30" y={118 + i * 44} width="16" height="16" rx="4"
            fill={item.done ? '#16a34a' : '#fff'}
            stroke={item.done ? '#16a34a' : '#cbd5e1'} strokeWidth="1.5" />
          {item.done && (
            <path d={`M ${34} ${126 + i * 44} l 4 4 6 -6`}
              stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          )}
          <text x="54" y={129 + i * 44} fill={item.done ? '#16a34a' : '#475569'} fontSize="10"
            fontFamily="Arial,sans-serif" fontWeight={item.done ? '600' : '400'}>{item.text}</text>
        </g>
      ))}
      <rect x="20" y="336" width="240" height="14" rx="7" fill="none" />
    </svg>
  );
}

/* ── Slider Slides data ──────────────────────────────────────── */
const SLIDES = [
  {
    badge: 'QR KOD SİSTEMİ',
    badgeColor: '#dbeafe',
    badgeTextColor: '#1d4ed8',
    bg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    title: 'Saniyeler içinde\ngeri bildirim alın',
    desc: 'Her lokasyon için kalıcı QR kod oluşturun. Ziyaretçiler uygulamaya ihtiyaç duymadan tarayıcıdan değerlendirir.',
    illustration: <PhoneIllustration />,
  },
  {
    badge: 'RAPORLAMA & ANALİZ',
    badgeColor: '#dcfce7',
    badgeTextColor: '#15803d',
    bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    title: 'Gerçek zamanlı\nveriler ve analizler',
    desc: 'Memnuniyet oranları, haftalık trendler ve sorunlu lokasyonları tek ekranda görün. Veri odaklı kararlar alın.',
    illustration: <DashboardIllustration />,
  },
  {
    badge: 'EKİP YÖNETİMİ',
    badgeColor: '#ede9fe',
    badgeTextColor: '#6d28d9',
    bg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
    title: 'Personel takibini\ndijitalleştirin',
    desc: 'Temizlik kontrol listelerini mobilde doldurun, tamamlanma durumlarını anlık izleyin ve raporlayın.',
    illustration: <ChecklistIllustration />,
  },
];

const FEATURES = [
  { icon: QrCode, title: 'Kalıcı QR Kod', desc: 'Değişmez bağlantılar. Yırtılsa da URL aynı kalır.' },
  { icon: MessageSquare, title: 'Anlık Bildirim', desc: 'Düşük puan gelince SMS ile anında haberdar olun.' },
  { icon: BarChart2, title: 'Detaylı Rapor', desc: 'Trendleri ve sorunları tek ekranda görün.' },
  { icon: CheckSquare, title: 'Checklist', desc: 'Personel kontrollerini dijitalleştirin.' },
  { icon: Globe, title: 'Çoklu Kurum', desc: 'Farklı kurumları tek platformdan yönetin.' },
  { icon: Building2, title: 'Çoklu Bina', desc: 'Tüm binalar ve katları hiyerarşik yönetin.' },
];

const STEPS = [
  { num: '01', title: 'Lokasyonlarınızı Ekleyin', desc: 'Binanızı sisteme tanımlayın. Her lokasyon için otomatik QR oluşturulsun.' },
  { num: '02', title: 'QR Kodu Asın', desc: 'Baskı hazır PDF olarak indirin ve tuvalet kapısına asın.' },
  { num: '03', title: 'Raporları Takip Edin', desc: 'Anlık bildirim alın, analiz edin, iyileştirin.' },
];

const SECTORS = ['🏥 Hastaneler', '🏫 Okullar', '🛍️ AVM', '🏨 Oteller', '✈️ Havalimanları', '🏢 Ofisler', '🏋️ Spor Tesisleri', '🍽️ Restoranlar'];

const DEFAULTS = {
  whatsapp_no: '', hero_baslik: 'Temizlik Hizmetlerinizi Dijitalleştirin',
  hero_aciklama: 'QR kod tabanlı anlık geri bildirim sistemi ile temizlik kalitenizi ölçün, raporlayın ve iyileştirin.',
  demo_mesaj: 'Merhaba, temiz-iz.com hakkında demo talep ediyorum.',
  iletisim_email: 'info@temiz-iz.com',
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULTS);
  const [scrolled, setScrolled] = useState(false);
  const [slide, setSlide] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    api.get('/site-ayarlari').then(({ data }) => {
      setSettings(s => ({ ...s, ...data }));
      if (data.seo_baslik) document.title = data.seo_baslik;
      if (data.seo_aciklama) {
        const m = document.querySelector('meta[name="description"]');
        if (m) m.setAttribute('content', data.seo_aciklama);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Auto-play slider
  useEffect(() => {
    const timer = setInterval(() => goTo((s) => (s + 1) % SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (indexOrFn) => {
    if (animating) return;
    setAnimating(true);
    setSlide(indexOrFn);
    setTimeout(() => setAnimating(false), 400);
  };

  const wpUrl = settings.whatsapp_no
    ? `https://wa.me/${settings.whatsapp_no}?text=${encodeURIComponent(settings.demo_mesaj || 'Demo talep ediyorum')}`
    : null;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const currentSlide = SLIDES[slide];

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", color: '#1e293b', overflowX: 'hidden' }}>

      {/* ── HEADER ── */}
      <header style={{ position: 'sticky', top: 0, zIndex: 200, background: scrolled ? 'rgba(255,255,255,0.96)' : '#fff', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${scrolled ? '#e2e8f0' : 'transparent'}`, boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.06)' : 'none', transition: 'all 0.25s' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', height: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/logo.png" alt="temiz-iz.com" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'contain' }} onError={e => e.target.style.display = 'none'} />
            <span style={{ fontWeight: 800, fontSize: 17, color: '#1d4ed8', letterSpacing: '-0.3px' }}>temiz-iz.com</span>
          </div>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 24 }} className="desktop-only">
            {[['ozellikler', 'Özellikler'], ['nasil-calisir', 'Nasıl Çalışır'], ['sektorler', 'Sektörler']].map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} style={{ background: 'none', border: 'none', color: '#475569', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }} onMouseEnter={e => e.target.style.color = '#1d4ed8'} onMouseLeave={e => e.target.style.color = '#475569'}>{label}</button>
            ))}
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 20 }}>
            {user ? (
              <button onClick={() => navigate('/dashboard')} style={{ padding: '8px 18px', background: 'linear-gradient(135deg,#1d4ed8,#4a6cf7)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>Panele Git</button>
            ) : (
              <>
                <Link to="/giris" style={{ color: '#475569', textDecoration: 'none', fontSize: 14, fontWeight: 500, padding: '8px 12px' }} className="desktop-only">Giriş Yap</Link>
                {wpUrl && (
                  <a href={wpUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', background: '#25D366', color: '#fff', textDecoration: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(37,211,102,0.35)', whiteSpace: 'nowrap' }}>
                    {WA_SVG} <span className="desktop-only" style={{ display: 'inline' }}>Demo Talep Et</span>
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section style={{ background: 'linear-gradient(145deg,#1e3a8a 0%,#1d4ed8 55%,#2563eb 100%)', padding: 'clamp(64px,10vw,110px) 20px clamp(72px,12vw,130px)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', top: -200, right: -150, pointerEvents: 'none' }} />
        <div style={{ maxWidth: 800, margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 16px', borderRadius: 100, marginBottom: 24 }}>
            <Shield size={13} color="#93c5fd" />
            <span style={{ color: '#93c5fd', fontSize: 12, fontWeight: 600 }}>KVKK Uyumlu · Güvenli · Türkiye'de Üretildi</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(28px,5.5vw,58px)', fontWeight: 800, lineHeight: 1.12, margin: '0 0 20px', letterSpacing: '-1px' }}>{settings.hero_baslik}</h1>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 'clamp(14px,2vw,18px)', lineHeight: 1.75, margin: '0 auto 40px', maxWidth: 600 }}>{settings.hero_aciklama}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {wpUrl && (
              <a href={wpUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '13px 28px', background: '#25D366', color: '#fff', textDecoration: 'none', borderRadius: 14, fontWeight: 700, fontSize: 15, boxShadow: '0 6px 24px rgba(37,211,102,0.45)' }}>
                {WA_SVG} Demo Talep Et
              </a>
            )}
            <Link to="/giris" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 26px', background: 'rgba(255,255,255,0.12)', color: '#fff', textDecoration: 'none', borderRadius: 14, fontWeight: 600, fontSize: 15, border: '1.5px solid rgba(255,255,255,0.3)' }}>
              Giriş Yap <ChevronRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── SLIDER ── */}
      <section style={{ padding: 'clamp(48px,7vw,80px) 20px', background: currentSlide.bg, transition: 'background 0.5s ease' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 40, alignItems: 'center' }}>
            {/* Text */}
            <div style={{ opacity: animating ? 0 : 1, transform: animating ? 'translateY(10px)' : 'none', transition: 'all 0.35s ease' }}>
              <div style={{ display: 'inline-block', background: currentSlide.badgeColor, color: currentSlide.badgeTextColor, padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 18 }}>
                {currentSlide.badge}
              </div>
              <h2 style={{ fontSize: 'clamp(24px,4vw,38px)', fontWeight: 800, lineHeight: 1.2, margin: '0 0 16px', letterSpacing: '-0.5px', whiteSpace: 'pre-line' }}>
                {currentSlide.title}
              </h2>
              <p style={{ color: '#475569', fontSize: 16, lineHeight: 1.75, margin: '0 0 32px' }}>
                {currentSlide.desc}
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {wpUrl && (
                  <a href={wpUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: '#1d4ed8', color: '#fff', textDecoration: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14 }}>
                    {WA_SVG} Demo İste
                  </a>
                )}
                <Link to="/giris" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 18px', background: 'rgba(29,78,216,0.08)', color: '#1d4ed8', textDecoration: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14 }}>
                  Giriş Yap <ChevronRight size={16} />
                </Link>
              </div>
            </div>
            {/* Illustration */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: animating ? 0 : 1, transform: animating ? 'scale(0.97)' : 'scale(1)', transition: 'all 0.35s ease' }}>
              {currentSlide.illustration}
            </div>
          </div>
          {/* Dots + Arrows */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 40 }}>
            <button onClick={() => goTo(s => (s - 1 + SLIDES.length) % SLIDES.length)} style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #cbd5e1', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <ChevronLeft size={16} color="#475569" />
            </button>
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => goTo(i)} style={{ width: i === slide ? 28 : 10, height: 10, borderRadius: 5, border: 'none', background: i === slide ? '#1d4ed8' : '#cbd5e1', cursor: 'pointer', transition: 'all 0.25s', padding: 0 }} />
            ))}
            <button onClick={() => goTo(s => (s + 1) % SLIDES.length)} style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #cbd5e1', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <ChevronRight size={16} color="#475569" />
            </button>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="ozellikler" style={{ padding: 'clamp(56px,8vw,96px) 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 14 }}>ÖZELLİKLER</div>
            <h2 style={{ fontSize: 'clamp(24px,4vw,38px)', fontWeight: 800, margin: '0 0 14px', letterSpacing: '-0.5px' }}>Her ihtiyacınıza yönelik çözüm</h2>
            <p style={{ color: '#64748b', fontSize: 16, maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>Temizlik yönetimini dijitalleştiren, anlık raporlayan ve iyileştiren platform.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 20 }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{ padding: '28px 24px', borderRadius: 16, border: '1.5px solid #e2e8f0', background: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', transition: 'all 0.2s', cursor: 'default' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 10px 30px rgba(29,78,216,0.12)'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <f.icon size={22} color="#1d4ed8" />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>{f.title}</h3>
                <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.75, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="nasil-calisir" style={{ padding: 'clamp(56px,8vw,96px) 20px', background: 'linear-gradient(180deg,#f0f9ff 0%,#e0f2fe 100%)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ display: 'inline-block', background: '#e0f2fe', color: '#0369a1', padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 14 }}>NASIL ÇALIŞIR</div>
            <h2 style={{ fontSize: 'clamp(24px,4vw,38px)', fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.5px' }}>3 adımda başlayın</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 32 }}>
            {STEPS.map(s => (
              <div key={s.num} style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#1d4ed8,#4a6cf7)', color: '#fff', fontSize: 18, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 6px 20px rgba(29,78,216,0.3)' }}>{s.num}</div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>{s.title}</h3>
                  <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.75, margin: 0 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTORS ── */}
      <section id="sektorler" style={{ padding: 'clamp(56px,8vw,96px) 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', background: '#f0fdf4', color: '#15803d', padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 14 }}>SEKTÖRLER</div>
          <h2 style={{ fontSize: 'clamp(24px,4vw,38px)', fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.5px' }}>Her sektöre uygun</h2>
          <p style={{ color: '#64748b', fontSize: 16, margin: '0 0 40px' }}>Farklı sektörlerde 50+ kurumun güveni.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            {SECTORS.map(s => (
              <div key={s} style={{ padding: '10px 20px', borderRadius: 100, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 14, fontWeight: 500, color: '#374151', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>{s}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: 'clamp(56px,8vw,96px) 20px', background: 'linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 60%,#4a6cf7 100%)', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <Smartphone size={44} color="rgba(255,255,255,0.4)" style={{ marginBottom: 18 }} />
          <h2 style={{ color: '#fff', fontSize: 'clamp(24px,4vw,38px)', fontWeight: 800, margin: '0 0 14px', letterSpacing: '-0.5px' }}>Ücretsiz demo alın</h2>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 17, margin: '0 0 36px', lineHeight: 1.7 }}>WhatsApp üzerinden anında yanıt alın, kurumunuza özel demo görün.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {wpUrl && (
              <a href={wpUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 32px', background: '#25D366', color: '#fff', textDecoration: 'none', borderRadius: 14, fontWeight: 700, fontSize: 16, boxShadow: '0 6px 28px rgba(37,211,102,0.45)' }}>
                {WA_SVG} WhatsApp ile Demo Talep Et
              </a>
            )}
            <Link to="/giris" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 26px', background: 'rgba(255,255,255,0.12)', color: '#fff', textDecoration: 'none', borderRadius: 14, fontWeight: 600, fontSize: 15, border: '1.5px solid rgba(255,255,255,0.3)' }}>
              Giriş Yap <ChevronRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#0f172a', padding: 'clamp(40px,6vw,56px) 20px 28px', color: '#94a3b8' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 36, marginBottom: 40 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <img src="/logo.png" alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }} onError={e => e.target.style.display = 'none'} />
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>temiz-iz.com</span>
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.8, margin: 0, maxWidth: 220 }}>QR kod tabanlı temizlik memnuniyet ve yönetim sistemi.</p>
            </div>
            <div>
              <div style={{ color: '#f1f5f9', fontWeight: 700, marginBottom: 16, fontSize: 13 }}>Hukuki</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Link to="/kvkk" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13 }}>KVKK Aydınlatma Metni</Link>
                <Link to="/gizlilik" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13 }}>Gizlilik Politikası</Link>
              </div>
            </div>
            <div>
              <div style={{ color: '#f1f5f9', fontWeight: 700, marginBottom: 16, fontSize: 13 }}>İletişim</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {settings.iletisim_email && <a href={`mailto:${settings.iletisim_email}`} style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13 }}>{settings.iletisim_email}</a>}
                {wpUrl && <a href={wpUrl} target="_blank" rel="noreferrer" style={{ color: '#4ade80', textDecoration: 'none', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>WhatsApp Destek</a>}
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12 }}>© {new Date().getFullYear()} temiz-iz.com · Tüm hakları saklıdır</span>
            <span style={{ fontSize: 12 }}>🇹🇷 Türkiye'de üretildi</span>
          </div>
        </div>
      </footer>

      {/* ── WHATSAPP FLOAT ── */}
      {wpUrl && (
        <a href={wpUrl} target="_blank" rel="noreferrer" title="WhatsApp"
          style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 999, width: 56, height: 56, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 28px rgba(37,211,102,0.55)', textDecoration: 'none', transition: 'transform 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
        </a>
      )}
    </div>
  );
}
